/**
 * FeedbackSheet — Emoji reactions + optional text feedback.
 *
 * Reusable feedback pattern using EntitySheet. Displays emoji quick-reactions
 * and an optional text field. Auto-collects page, browser, and user info.
 *
 * Usage:
 *   <FeedbackSheet isOpen={open} onClose={() => setOpen(false)} />
 */

import { useState, useCallback, useEffect } from 'react';
import { ds } from '../tokens';
import { TextField } from '../primitives/TextField';
import { EntitySheet } from './EntitySheet';
import { FEEDBACK } from '../../shared/constants/marketingContent';
import { feedbackService, type FeedbackType } from '../../shared/services/feedbackService';
import { useProfile } from '../../shared/contexts/ProfileContext';
import { showToast } from '../../shared/components/ui/Toast';

export interface FeedbackSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FeedbackSheet({ isOpen, onClose }: FeedbackSheetProps) {
  const { pilotInfo } = useProfile();

  const [selectedEmoji, setSelectedEmoji] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState('');
  const [loading, setLoading] = useState(false);

  // Prevent browser from auto-focusing the textarea on sheet open
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      if (
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLInputElement
      ) {
        document.activeElement.blur();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const reset = useCallback(() => {
    setSelectedEmoji(null);
    setMessage('');
    setMessageError('');
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!selectedEmoji && !message.trim()) {
      setMessageError('Please select an emoji or enter feedback.');
      return;
    }
    setMessageError('');
    setLoading(true);

    const emojiLabel =
      FEEDBACK.EMOJI_OPTIONS.find((e) => e.emoji === selectedEmoji)?.label ?? '';
    const feedbackMessage = [
      selectedEmoji ? `${selectedEmoji} ${emojiLabel}` : '',
      message.trim(),
    ]
      .filter(Boolean)
      .join(' — ');

    const title = `[Feedback] ${feedbackMessage.slice(0, 50)}${feedbackMessage.length > 50 ? '...' : ''}`;

    try {
      await feedbackService.submitFeedback(
        {
          type: 'general' as FeedbackType,
          title,
          description: feedbackMessage,
          images: [],
        },
        {
          email: pilotInfo?.email,
          name: pilotInfo?.fullName,
          userId: localStorage.getItem('lucidDock_userId') || undefined,
        },
      );
      showToast.success(FEEDBACK.SUCCESS_MESSAGE);
      handleClose();
    } catch {
      showToast.error(FEEDBACK.ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, [selectedEmoji, message, pilotInfo, handleClose]);

  return (
    <EntitySheet
      size="sm"
      isOpen={isOpen}
      onClose={handleClose}
      title={FEEDBACK.TITLE}
      onSubmit={handleSubmit}
      submitLabel={FEEDBACK.SUBMIT_LABEL}
      submitDisabled={loading}
      loading={loading}
    >
      <div className={ds.layout.formGap}>
        {/* Emoji quick reactions */}
        <div>
          <p className={`${ds.type.footnote} ${ds.color.label.secondary} mb-2`}>
            {FEEDBACK.RATE_LABEL}
          </p>
          <div className="flex justify-center gap-3">
            {FEEDBACK.EMOJI_OPTIONS.map(({ emoji, label }) => (
              <button
                key={emoji}
                type="button"
                onClick={() =>
                  setSelectedEmoji(selectedEmoji === emoji ? null : emoji)
                }
                className={`flex flex-col items-center gap-1 px-3 py-2 ${ds.radius.lg} ${ds.transition.fast} cursor-pointer group`}
                style={{
                  backgroundColor:
                    selectedEmoji === emoji
                      ? ds.color.raw.systemBlue + '1A'
                      : 'transparent',
                  border: `1px solid ${
                    selectedEmoji === emoji
                      ? ds.color.raw.systemBlue
                      : 'transparent'
                  }`,
                }}
              >
                <span className={`${ds.transition.fast} group-hover:scale-110`} style={{ fontSize: 28 }}>{emoji}</span>
                <span
                  className={`${ds.type.caption2} ${
                    selectedEmoji === emoji ? ds.type.weight.semibold : ''
                  }`}
                  style={{
                    color:
                      selectedEmoji === emoji
                        ? ds.color.raw.systemBlue
                        : ds.color.raw.systemGray,
                  }}
                >
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Optional message */}
        <TextField
          label={FEEDBACK.MESSAGE_LABEL}
          value={message}
          onChange={(e) =>
            setMessage(e.target.value.slice(0, FEEDBACK.MESSAGE_MAX_LENGTH))
          }
          multiline
          rows={3}
          maxLength={FEEDBACK.MESSAGE_MAX_LENGTH}
          error={messageError}
        />
      </div>
    </EntitySheet>
  );
}
